import { useState, useEffect, useCallback, useRef } from 'react';
import {
  LAST_ACTIVITY_STORAGE_KEY,
  INACTIVITY_TIMEOUT_MS,
  INACTIVITY_WARNING_MS,
  ACTIVITY_DEBOUNCE_MS,
  ACTIVITY_BROADCAST_CHANNEL,
} from '@/utils/constants';

interface ActivityTrackerState {
  lastActivity: number;
  remainingTime: number;
  isWarningVisible: boolean;
  isTimedOut: boolean;
}

interface ActivityTrackerActions {
  extendSession: () => void;
  resetActivity: () => void;
}

type ActivityMessage =
  | { type: 'ACTIVITY'; timestamp: number }
  | { type: 'EXTEND_SESSION'; timestamp: number }
  | { type: 'LOGOUT' };

/**
 * Hook to track user activity and manage inactivity timeout
 *
 * Features:
 * - Tracks mouse, keyboard, touch, and scroll events
 * - Debounces activity updates to prevent excessive processing
 * - Cross-tab synchronization via BroadcastChannel API
 * - localStorage fallback for Safari compatibility
 * - Triggers warning before timeout
 */
export function useActivityTracker(
  enabled: boolean = true
): ActivityTrackerState & ActivityTrackerActions {
  const [state, setState] = useState<ActivityTrackerState>({
    lastActivity: Date.now(),
    remainingTime: INACTIVITY_TIMEOUT_MS,
    isWarningVisible: false,
    isTimedOut: false,
  });

  const lastActivityRef = useRef<number>(Date.now());
  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const broadcastChannelRef = useRef<BroadcastChannel | null>(null);

  // Initialize last activity from storage
  useEffect(() => {
    if (!enabled) { return; }

    const stored = localStorage.getItem(LAST_ACTIVITY_STORAGE_KEY);
    if (stored) {
      const storedTime = parseInt(stored, 10);
      if (!isNaN(storedTime) && storedTime > 0) {
        lastActivityRef.current = storedTime;
        setState(prev => ({ ...prev, lastActivity: storedTime }));
      }
    } else {
      // Set initial activity timestamp
      const now = Date.now();
      localStorage.setItem(LAST_ACTIVITY_STORAGE_KEY, now.toString());
      lastActivityRef.current = now;
    }
  }, [enabled]);

  // Set up BroadcastChannel for cross-tab communication
  useEffect(() => {
    if (!enabled) { return; }

    try {
      broadcastChannelRef.current = new BroadcastChannel(ACTIVITY_BROADCAST_CHANNEL);

      broadcastChannelRef.current.onmessage = (event: MessageEvent<ActivityMessage>) => {
        const message = event.data;

        switch (message.type) {
          case 'ACTIVITY':
          case 'EXTEND_SESSION':
            // Update local state when activity happens in another tab
            lastActivityRef.current = message.timestamp;
            localStorage.setItem(LAST_ACTIVITY_STORAGE_KEY, message.timestamp.toString());
            setState(prev => ({
              ...prev,
              lastActivity: message.timestamp,
              isWarningVisible: false,
              isTimedOut: false,
            }));
            break;
          case 'LOGOUT':
            // Another tab triggered logout
            setState(prev => ({ ...prev, isTimedOut: true }));
            break;
        }
      };
    } catch {
      // BroadcastChannel not supported (Safari in some contexts)
      // Fall back to storage events
    }

    // Storage event listener for Safari fallback
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === LAST_ACTIVITY_STORAGE_KEY && event.newValue) {
        const newTime = parseInt(event.newValue, 10);
        if (!isNaN(newTime) && newTime > lastActivityRef.current) {
          lastActivityRef.current = newTime;
          setState(prev => ({
            ...prev,
            lastActivity: newTime,
            isWarningVisible: false,
            isTimedOut: false,
          }));
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      if (broadcastChannelRef.current) {
        broadcastChannelRef.current.close();
        broadcastChannelRef.current = null;
      }
    };
  }, [enabled]);

  // Broadcast activity to other tabs
  const broadcastActivity = useCallback((timestamp: number, isExtend: boolean = false) => {
    const message: ActivityMessage = {
      type: isExtend ? 'EXTEND_SESSION' : 'ACTIVITY',
      timestamp,
    };

    if (broadcastChannelRef.current) {
      try {
        broadcastChannelRef.current.postMessage(message);
      } catch {
        // Channel may be closed
      }
    }

    // Also update localStorage for Safari fallback
    localStorage.setItem(LAST_ACTIVITY_STORAGE_KEY, timestamp.toString());
  }, []);

  // Broadcast logout to other tabs
  const broadcastLogout = useCallback(() => {
    const message: ActivityMessage = { type: 'LOGOUT' };

    if (broadcastChannelRef.current) {
      try {
        broadcastChannelRef.current.postMessage(message);
      } catch {
        // Channel may be closed
      }
    }

    // Clear activity from storage
    localStorage.removeItem(LAST_ACTIVITY_STORAGE_KEY);
  }, []);

  // Record activity with debouncing
  const recordActivity = useCallback(() => {
    if (!enabled || state.isTimedOut) { return; }

    // Clear existing debounce timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Debounce activity updates
    debounceTimeoutRef.current = setTimeout(() => {
      const now = Date.now();
      lastActivityRef.current = now;

      setState(prev => ({
        ...prev,
        lastActivity: now,
        isWarningVisible: false,
      }));

      broadcastActivity(now);
    }, ACTIVITY_DEBOUNCE_MS);
  }, [enabled, state.isTimedOut, broadcastActivity]);

  // Set up activity event listeners
  useEffect(() => {
    if (!enabled) { return; }

    const activityEvents = [
      'mousemove',
      'mousedown',
      'keydown',
      'touchstart',
      'scroll',
      'click',
    ];

    // Add event listeners
    activityEvents.forEach(event => {
      window.addEventListener(event, recordActivity, { passive: true });
    });

    // Handle visibility change - record activity when tab becomes visible
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        recordActivity();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      activityEvents.forEach(event => {
        window.removeEventListener(event, recordActivity);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);

      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [enabled, recordActivity]);

  // Countdown timer
  useEffect(() => {
    if (!enabled) { return; }

    const updateCountdown = () => {
      const now = Date.now();
      const elapsed = now - lastActivityRef.current;
      const remaining = Math.max(0, INACTIVITY_TIMEOUT_MS - elapsed);
      const warningThreshold = INACTIVITY_WARNING_MS;

      setState(prev => {
        // Check if timed out
        if (remaining <= 0 && !prev.isTimedOut) {
          broadcastLogout();
          return {
            ...prev,
            remainingTime: 0,
            isWarningVisible: false,
            isTimedOut: true,
          };
        }

        // Check if should show warning
        const shouldShowWarning = remaining <= warningThreshold && remaining > 0;

        return {
          ...prev,
          remainingTime: remaining,
          isWarningVisible: shouldShowWarning,
        };
      });
    };

    // Update every second
    countdownIntervalRef.current = setInterval(updateCountdown, 1000);

    // Initial update
    updateCountdown();

    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
    };
  }, [enabled, broadcastLogout]);

  // Extend session action
  const extendSession = useCallback(() => {
    const now = Date.now();
    lastActivityRef.current = now;

    setState({
      lastActivity: now,
      remainingTime: INACTIVITY_TIMEOUT_MS,
      isWarningVisible: false,
      isTimedOut: false,
    });

    broadcastActivity(now, true);
  }, [broadcastActivity]);

  // Reset activity (used on login)
  const resetActivity = useCallback(() => {
    const now = Date.now();
    lastActivityRef.current = now;
    localStorage.setItem(LAST_ACTIVITY_STORAGE_KEY, now.toString());

    setState({
      lastActivity: now,
      remainingTime: INACTIVITY_TIMEOUT_MS,
      isWarningVisible: false,
      isTimedOut: false,
    });
  }, []);

  return {
    ...state,
    extendSession,
    resetActivity,
  };
}

/**
 * Format remaining time as MM:SS string
 */
export function formatRemainingTime(ms: number): string {
  const totalSeconds = Math.ceil(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}
